import DOMPurify from "dompurify";
import { Spinner } from "react-bootstrap";
import styled from "styled-components";

import {
  Currency,
  LicenceType,
  PricingRule,
  SoftwareLicenceSummary,
  SoftwareLicenceSummaryKeys,
  TimeUnit,
} from "@sso/types";
import { Button } from "@sso/ui/Button";
import { Typography } from "@sso/ui/Typography";

import { branding } from "./branding";
import "./branding.css";
import SingleUserPricing from "./SingleUserPricing";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-right: 1px solid #ccc;
  border-left: 1px solid #ccc;
  height: 100%;
  width: 325px;
  gap: 1rem;
`;

const LicenceCardHeader = styled.div`
  border-bottom: 1px solid #ccc;
  text-align: center;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LicenceCardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
  gap: 1rem;
  background-color: white;
  flex-grow: 1;
`;

const LicenceCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1rem;
`;

const LicenceCardPricing = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

interface LicenceFeaturesProps {
  bulletColor?: string;
}

const LicenceFeatures = styled.div<LicenceFeaturesProps>`
  display: flex;
  flex-grow: 1;

  ul {
    list-style: none; /* Remove default bullets */
    padding-left: 1.5em;
    margin: 1em 0;
  }

  ul li {
    position: relative;
    margin-bottom: 0.5em;
    padding-left: 1em;
  }

  ul li::before {
    content: "•"; /* Unicode bullet */
    position: absolute;
    left: 0;
    color: ${({ bulletColor }) => bulletColor || "black"};
    font-size: 1.2em;
    line-height: 1;
  }
`;

const getHeaderColor = (licenceName: string): string => {
  const brand = branding.find(b => licenceName.toLowerCase().includes(b.name.toLowerCase()));
  return brand ? "white" : "black"; // Default to black if no brand found
};

const getBulletColor = (color?: string) => {
  switch (color) {
    case "gold":
      return "#c7a624";
    case "silver":
      return "#7a7a7a";
    case "bronze":
      return "#cd7f32";
    default:
      return color || "black"; // fallback to passed color or black
  }
};

interface ListItemProps {
  bulletColor?: string;
}

interface LicenceCardProps {
  licence: SoftwareLicenceSummary;
  transactionCurrency: Currency;
  onSubscribe: (licence: SoftwareLicenceSummary) => void;
  loading: boolean;
}

const getExpiryLabel = (licence: SoftwareLicenceSummary): string => {
  const value = licence[SoftwareLicenceSummaryKeys.EXPIRY_VALUE];
  const unit = licence[SoftwareLicenceSummaryKeys.EXPIRY_UNIT].toUpperCase();
  if (value === 1) {
    switch (unit) {
      case TimeUnit.Day:
        return "Per Day";
      case TimeUnit.Month:
        return "Per Month";
      case TimeUnit.Year:
        return "Per Year";
    }
  }

  if (value > 1) {
    switch (unit) {
      case TimeUnit.Day.toLowerCase():
        return `Per ${value} Days`;
      case TimeUnit.Month:
        return `Per ${value} Months`;
      case TimeUnit.Year.toLowerCase():
        return `Per ${value} Years`;
    }
  }

  return "Invalid Expiry";
};

const LicenceCard = ({ licence, transactionCurrency, onSubscribe, loading }: LicenceCardProps) => {
  const name = licence.name.replace(/\s*\(.*?\)\s*/g, "").trim();
  const brand = branding.find(b => licence.name.toLowerCase().includes(b.name.toLowerCase()));
  const isConcurrent = licence.licenceType === LicenceType.CONCURRENT;
  
  const getPricing = (licence: SoftwareLicenceSummary, currency: Currency, pricingRules?: PricingRule[]) => {
    if (licence.licenceType === LicenceType.TRIAL) {
      return (
        <Typography size={"1.2"} fontWeight={400}>
          Free
        </Typography>
      );
    }
    if (!pricingRules || pricingRules.length === 0) {
      return <span>Pricing Not Available</span>;
    }
      return (
        <>
          <SingleUserPricing
            licenceType={licence.licenceType}
            transactionCurrency={currency}
            pricingRules={pricingRules!}
          />
          <span>{getExpiryLabel(licence)}</span>
        </>
      );
    
  };
  const featureFontSize: number = 0.71;
  const featureFontWeight = 200;
  return (
    <Container className="bg-light" key={licence.softwareLicenceId}>
      <LicenceCardHeader style={{ height: "50px" }} className={brand?.className}>
        <Typography color={getHeaderColor(licence.name)} fontWeight={400} size={"1.3"} className="mb-0">
          {name}
        </Typography>
      </LicenceCardHeader>
      <LicenceCardContent className="bg-light">
        <LicenceFeatures bulletColor={getBulletColor(licence.name.toLowerCase())}>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(licence.featureHtml),
            }}
          />
        </LicenceFeatures>
        {
            licence.licenceType !== LicenceType.CONCURRENT &&(
            <LicenceCardPricing className="bg-light">
              {loading ? <Spinner /> : getPricing(licence, transactionCurrency, licence.pricingRules)}
            </LicenceCardPricing>
            )
        }  
        
      </LicenceCardContent>
      <LicenceCardFooter className={`${brand?.className}-footer`}>
        {
            isConcurrent ? 
            (
               <div className="d-flex flex-column justify-content-end align-items-center h-100 text-center">
                    Contact Basemap{"  "}
                    <a href="mailto:sales@basemap.co.uk">sales@basemap.co.uk</a>
              </div>
            ) : 
            (
                <Button
                  className="btn-local"
                  disabled={loading}
                  onClick={() => onSubscribe(licence)}
                >
                  {loading ? <Spinner /> : <span>Subscribe</span>}
              </Button>
            )
          }
      </LicenceCardFooter>
    </Container>
  );
};

export default LicenceCard;
